import {
    ExperiencesSelection,
    ActivitiesSelection,
    ExperiencesDestination,
    ActivitiesDestination,
    ExperiencesSelectionEnum,
    ActivitiesSelectionEnum,
    IIdExperiences,
    IIdActivities,
    Id,
    IdEnum,
} from "redmond";

/**
 * Type guard to check if an Id is IIdActivities
 */
export function isActivitiesId(id: Id): id is IIdActivities {
    return id.Id === IdEnum.Activities;
}

/**
 * Type guard to check if an Id is IIdExperiences
 */
export function isExperiencesId(id: Id): id is IIdExperiences {
    return id.Id === IdEnum.Experiences;
}

/**
 * Type guard to check if a selection is an ActivitiesSelection
 */
export function isActivitiesSelection(
    selection: any
): selection is ActivitiesSelection {
    return selection?.ActivitiesSelection !== undefined;
}

/**
 * Type guard to check if a selection is an ExperiencesSelection
 */
export function isExperiencesSelection(
    selection: any
): selection is ExperiencesSelection {
    return selection?.ExperiencesSelection !== undefined;
}

/**
 * Convert ExperiencesSelection to ActivitiesSelection
 */
export function toActivitiesSelection(
    experiencesSelection: ExperiencesSelection
): ActivitiesSelection {
    if (experiencesSelection.ExperiencesSelection === ExperiencesSelectionEnum.ExperiencesDestination) {
        const destination = experiencesSelection as ExperiencesDestination;
        return {
            id: destination.id,
            name: destination.name,
            searchTerm: destination.searchTerm,
            ActivitiesSelection: ActivitiesSelectionEnum.ActivitiesDestination,
            thumbnailUrl: destination.thumbnailUrl,
            activityCount: destination.activityCount,
            suggestionType: destination.suggestionType,
        } as ActivitiesDestination;
    }
    // Add other selection type conversions as needed
    throw new Error(`Unsupported ExperiencesSelection type: ${experiencesSelection.ExperiencesSelection}`);
}

/**
 * Convert ActivitiesSelection to ExperiencesSelection
 */
export function toExperiencesSelection(
    activitiesSelection: ActivitiesSelection
): ExperiencesSelection {
    if (activitiesSelection.ActivitiesSelection === ActivitiesSelectionEnum.ActivitiesDestination) {
        const destination = activitiesSelection as ActivitiesDestination;
        return {
            id: destination.id,
            name: destination.name,
            searchTerm: destination.searchTerm,
            ExperiencesSelection: ExperiencesSelectionEnum.ExperiencesDestination,
            thumbnailUrl: destination.thumbnailUrl,
            activityCount: destination.activityCount,
            suggestionType: destination.suggestionType,
        } as ExperiencesDestination;
    }
    // Add other selection type conversions as needed
    throw new Error(`Unsupported ActivitiesSelection type: ${activitiesSelection.ActivitiesSelection}`);
}

/**
 * Get the appropriate selection from Id (either IIdExperiences or IIdActivities)
 */
export function getSelection(id: Id): ExperiencesSelection | ActivitiesSelection {
    if (isExperiencesId(id)) {
        return id.experiencesSelection as ExperiencesSelection;
    } else if (isActivitiesId(id)) {
        return id.activitiesSelection as ActivitiesSelection;
    }
    throw new Error("Unknown Id type");
}

/**
 * Get both selections from an Id, converting if necessary
 */
export function getBothSelections(id: Id): {
    experiencesSelection: ExperiencesSelection;
    activitiesSelection: ActivitiesSelection;
} {
    const selection = getSelection(id);

    if (isExperiencesSelection(selection)) {
        return {
            experiencesSelection: selection,
            activitiesSelection: toActivitiesSelection(selection),
        };
    } else if (isActivitiesSelection(selection)) {
        return {
            experiencesSelection: toExperiencesSelection(selection),
            activitiesSelection: selection,
        };
    }

    throw new Error("Unknown selection type");
}

/**
 * Get the selection name from Id
 * Works with both IIdExperiences and IIdActivities
 */
export function getSelectionName(id: Id): string {
    const selection = getSelection(id);

    if (isExperiencesSelection(selection)) {
        return (selection as ExperiencesDestination).name;
    } else if (isActivitiesSelection(selection)) {
        return (selection as ActivitiesDestination).name;
    }

    throw new Error("Unknown selection type");
}

/**
 * Get the selection id from Id
 * Works with both IIdExperiences and IIdActivities
 */
export function getSelectionId(id: Id): string {
    const selection = getSelection(id);

    if (isExperiencesSelection(selection)) {
        return (selection as ExperiencesDestination).id;
    } else if (isActivitiesSelection(selection)) {
        return (selection as ActivitiesDestination).id;
    }

    throw new Error("Unknown selection type");
}